/**
 * AuditExportButton — FR-085 compliance export dropdown (PDF / CSV).
 * Calls GET /api/v1/audit/export?format=pdf|csv&<filters>.
 * Exports honour the active filter set. Cap: 10,000 rows.
 * WCAG 2.1 AA: aria-label on trigger, progress indication, status messages.
 */
import { useState, useRef } from 'react';
import { Download, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { cn } from '@/lib/utils';
import type { AuditFilters } from './AuditLogFilters';

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface AuditExportButtonProps {
  filters: AuditFilters;
  className?: string;
}

type ExportFormat = 'pdf' | 'csv';

async function triggerExport(format: ExportFormat, filters: AuditFilters): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;

  const params = new URLSearchParams({ format });
  if (filters.eventTypes.length > 0) params.set('event_type', filters.eventTypes.join(','));
  if (filters.dateFrom) params.set('date_from', filters.dateFrom);
  if (filters.dateTo) params.set('date_to', filters.dateTo);
  if (filters.jobId.trim()) params.set('job_id', filters.jobId.trim());
  if (filters.search.trim()) params.set('search', filters.search.trim());

  const res = await fetch(`${API_BASE}/api/v1/audit/export?${params.toString()}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    if (res.status === 400) {
      throw new Error('Too many rows. Please apply a date range filter.');
    }
    throw new Error('Export failed. Please narrow the filter range and try again.');
  }

  const contentDisposition = res.headers.get('Content-Disposition') ?? '';
  const filenameMatch = /filename="?([^"]+)"?/.exec(contentDisposition);
  const filename =
    filenameMatch?.[1] ??
    `cmp_audit_${new Date().toISOString().slice(0, 10)}.${format}`;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
  toast.success(`Export ready: ${filename}`);
}

export function AuditExportButton({ filters, className }: AuditExportButtonProps) {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const handleExport = async (format: ExportFormat) => {
    setOpen(false);
    setGenerating(true);
    try {
      await triggerExport(format, filters);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Export failed.';
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className={cn('relative inline-flex flex-col items-end gap-1', className)}>
      <div className="relative inline-flex">
        {/* Main export button */}
        <Button
          variant="outline"
          size="sm"
          disabled={generating}
          onClick={() => { setOpen((v) => !v); }}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Export audit log"
          onKeyDown={handleKeyDown}
        >
          {generating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              Generating…
            </>
          ) : (
            <>
              <Download className="mr-2 h-4 w-4" aria-hidden="true" />
              Export
              <ChevronDown className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
            </>
          )}
        </Button>

        {/* Dropdown menu */}
        {open && !generating && (
          <div
            ref={menuRef}
            role="menu"
            aria-label="Export format options"
            className="absolute right-0 top-full mt-1 z-10 min-w-[160px] rounded-md border border-border bg-background shadow-md"
            onKeyDown={handleKeyDown}
          >
            <button
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
              onClick={() => void handleExport('pdf')}
            >
              Export as PDF
            </button>
            <button
              role="menuitem"
              className="flex w-full items-center gap-2 px-4 py-2 text-sm text-foreground hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
              onClick={() => void handleExport('csv')}
            >
              Export as CSV
            </button>
          </div>
        )}
      </div>
      <p className="text-xs text-muted-foreground max-w-xs text-right">
        Exports only include entries currently visible with the active filters (up to 10,000 rows).
      </p>
    </div>
  );
}
